import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useOrganizationLabels } from '../../../../hooks/api/organization/useOrganizationLabels';
import MultiSelectFilter from '../../../generic/Filter/MultiSelectFilter';
import Tooltip from '../../../generic/Tooltip';
import type { Label } from '../../../../../shared/labels';
import type { MultiSelectFilterOption } from '../../../generic/Filter/MultiSelectFilter';

const labelsToOptions = (labels: Label[]): MultiSelectFilterOption[] => {
  return labels.map(l => ({ label: l.label, value: l.label }));
};

const stringsToOptions = (strings: string[]): MultiSelectFilterOption[] => {
  return strings.map(s => ({ label: s, value: s }));
};

type Props = {
  filterLabels: string[];
  onChange: (items: MultiSelectFilterOption[]) => void;
  testId?: string;
};

/**
 * Multi-select filter for Project Labels. Fetches the organization's labels via React Query
 * (the query is cached and shared across the page, so the menu and filter read from one fetch).
 * If the fetch fails the filter is disabled and a tooltip explains why on hover.
 * @param {string[]} filterLabels - The labels that the user has selected.
 * @param {(items: MultiSelectFilterOption[]) => void} onChange - The function to call when the user changes their selections.
 * @param {string} [testId] - The test ID. Mainly used for Selenium testing.
 */
const ProjectLabels: VoidFunctionComponent<Props> = ({
  filterLabels,
  onChange,
  testId,
}) => {
  const { data, isError } = useOrganizationLabels();
  const options = useMemo(() => labelsToOptions(data?.labels ?? []), [data]);

  return (
    <Tooltip
      id="project-labels-filter-error-tooltip"
      tooltip="Project Labels failed to load"
      showTooltip={isError}
    >
      <div className="width-full">
        <MultiSelectFilter
          title="Labels"
          placeholder="Select labels..."
          values={stringsToOptions(filterLabels)}
          options={options}
          isDisabled={isError}
          onChange={onChange}
          testId={testId}
        />
      </div>
    </Tooltip>
  );
};

export default ProjectLabels;
